<script setup lang="ts">
import RadBadge from '../atoms/RadBadge.vue';
import RadIcon from '../atoms/RadIcon.vue';
import RadCard from '../molecules/RadCard.vue';
import type { Feature } from './RadFeatureGrid.data';
import { defaultFeatures } from './RadFeatureGrid.data';

interface Props {
  features: Feature[];
  sectionId?: string;
  title?: string;
  subtitle?: string;
}

withDefaults(defineProps<Props>(), {
  sectionId: 'features',
  title: '',
  subtitle: '',
});
defineExpose({ defaultFeatures });
</script>

<template>
  <section :id="sectionId" class="py-24 px-4 bg-white dark:bg-slate-950 relative overflow-hidden">
    <!-- Background Decoration -->
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-rad-blue/5 blur-[120px] rounded-full -z-10"></div>

    <div class="max-w-7xl mx-auto">
      <div v-if="title || subtitle" class="text-center mb-16">
        <h2 v-if="title" class="text-3xl md:text-5xl font-bold text-slate-900 dark:text-rad-white mb-4 tracking-tight" v-html="title"></h2>
        <p v-if="subtitle" class="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto text-lg">
          {{ subtitle }}
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <RadCard
          v-for="feature in features"
          :key="feature.title"
          hoverable
          class="group"
        >
          <div class="flex items-start justify-between gap-4 mb-6">
            <div
              class="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3"
              :class="[
                feature.color === 'red' && 'bg-red-500/20 text-primary',
                feature.color === 'blue' && 'bg-blue-500/20 text-secondary',
                feature.color === 'emerald' && 'bg-emerald-500/20 text-emerald-400',
                feature.color === 'amber' && 'bg-amber-500/20 text-amber-400',
                feature.color === 'purple' && 'bg-purple-500/20 text-purple-400',
                feature.color === 'cyan' && 'bg-cyan-500/20 text-cyan-400'
              ]"
            >
              <RadIcon :name="feature.icon" :size="24" />
            </div>
            <RadBadge v-if="feature.badge" variant="primary" size="sm">
              {{ feature.badge }}
            </RadBadge>
          </div>
          <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white mb-3 group-hover:text-rad-red transition-colors">
            {{ feature.title }}
          </h3>
          <p class="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            {{ feature.description }}
          </p>
          <ul v-if="feature.items && feature.items.length" class="space-y-2 text-sm text-slate-500 dark:text-slate-500">
            <li v-for="item in feature.items" :key="item" class="flex items-center gap-2">
              <RadIcon name="CheckIcon" :size="14" class="text-emerald-400" />
              {{ item }}
            </li>
          </ul>
        </RadCard>
      </div>
    </div>
  </section>
</template>
